import type { DbExecutor } from "../executor";
import type { AssemblyRepository } from "../repositories/assemblyRepository";
import type { ExpenseRepository } from "../repositories/expenseRepository";
import type { PurchaseRepository } from "../repositories/purchaseRepository";
import type { MileageRepository } from "../repositories/mileageRepository";
import { projectActualEvents, type ActualCostEvent } from "../../engine/actualEvents";
import { summarize } from "../../engine/estimateEvidence";
import type {
  CostEvidence,
  EstimateRequest,
  EvidenceObservation,
  EvidenceProvider,
} from "../../estimating/evidence";

/**
 * ORGANIZATION HISTORY — what this company actually paid (spec §26D).
 *
 * The heart of Phase 9E: instead of "the template says a toilet costs X",
 * this answers "the last three toilets we installed cost us Y, Z and W".
 *
 * Discipline it inherits rather than reinvents:
 *  - actual money comes from the SAME normalized ActualCostEvent stream the
 *    engine and Phase 9C use — there is no second opinion about what was
 *    spent;
 *  - a cost basis never crosses (material evidence cannot price labor), and
 *    money whose basis was never stated is skipped, not guessed;
 *  - quantities come from the assembly engine's own calculation, so a
 *    per-unit figure means the same thing it means in an estimate;
 *  - amounts EXCLUDE sales tax, because the estimate adds estimating tax
 *    itself — including it would inflate every future estimate;
 *  - the project being estimated is excluded: an estimate must not learn
 *    from itself;
 *  - unfinished work is reported PROVISIONAL and never becomes a
 *    recommendation.
 */
export class OrganizationHistoryProvider implements EvidenceProvider {
  readonly id = "organization-history";
  readonly label = "Company history";
  readonly kind = "organization-history" as const;

  constructor(
    private readonly db: DbExecutor,
    private readonly assemblies: AssemblyRepository,
    private readonly expenses: ExpenseRepository,
    private readonly purchases: PurchaseRepository,
    private readonly mileage: MileageRepository,
  ) {}

  async availability() {
    const rows = await this.db.select<{ n: number }>(
      "SELECT COUNT(*) AS n FROM purchase_allocations",
    );
    const n = Number(rows[0]?.n ?? 0);
    return {
      available: true,
      note:
        n === 0
          ? "No purchases recorded yet — history grows as you record actual costs."
          : null,
    };
  }

  /** Actual events for one project, cached for the life of the request. */
  private async eventsFor(projectId: string, cache: Map<string, ActualCostEvent[]>): Promise<ActualCostEvent[]> {
    const hit = cache.get(projectId);
    if (hit) return hit;
    const [expenses, graphs, mileage] = await Promise.all([
      this.expenses.listByProject(projectId),
      this.purchases.listTouchingProject(projectId),
      this.mileage.listByProject(projectId),
    ]);
    const events = projectActualEvents(projectId, expenses, graphs, mileage).filter((e) => e.voidedAt === null);
    cache.set(projectId, events);
    return events;
  }

  async gather(request: EstimateRequest): Promise<CostEvidence[]> {
    const { subject } = request;
    if (subject.featureDefinitionId === null) return [];
    if (subject.kind === "component" && subject.componentName === null) return [];

    const rows = await this.candidateLines(request);
    if (rows.length === 0) return [];

    /*
     * WORK THE COMPANY DOES NOT WANT TO LEARN FROM (spec §90-37): a job with
     * donated labour, a favour, a fire rebuild. The exclusion is a DECISION
     * stored on its own — the accounting, the actual and the historical
     * project are untouched; estimating simply stops counting that line.
     */
    const excluded = new Set(
      (await this.db.select<{ proposed_line_id: string }>("SELECT proposed_line_id FROM estimating_history_exclusions")).map(
        (r) => r.proposed_line_id,
      ),
    );

    const eventCache = new Map<string, ActualCostEvent[]>();
    const instanceQty = new Map<string, Map<string, number>>();
    const settled: EvidenceObservation[] = [];
    const provisional: EvidenceObservation[] = [];

    for (const row of rows) {
      if (excluded.has(row.line_id)) continue;
      const events = await this.eventsFor(row.project_id, eventCache);
      const relevant = events.filter(
        (e) =>
          e.linkedProposedLineId === row.line_id &&
          // Basis never crosses; unstated basis is skipped, never assumed.
          e.costType !== null &&
          e.costType === subject.costBasis,
      );
      if (relevant.length === 0) continue;
      // Pre-tax (already includes allocated shipping) — the estimate adds
      // estimating tax on top, so including sales tax here would double it.
      const actualCents = relevant.reduce((sum, e) => sum + e.amountCents, 0);
      if (actualCents <= 0) continue;

      const qtyX100 = await this.quantityFor(row, instanceQty);
      if (qtyX100 === null || qtyX100 <= 0) continue;

      const observation: EvidenceObservation = {
        label: `${row.feature_name} — ${row.description}`,
        unitCostCentsX100: Math.round((actualCents * 10000) / qtyX100),
        quantityX100: qtyX100,
        date: relevant.map((e) => e.date).sort().slice(-1)[0] ?? "",
        projectJobNumber: row.job_number,
        projectId: row.project_id,
        market: row.zip || null,
        ref: this.refFor(relevant),
      };
      if (row.work_status === "complete") settled.push(observation);
      else provisional.push(observation);
    }

    const out: CostEvidence[] = [];
    const complete = this.toEvidence(settled, request, false);
    if (complete) out.push(complete);
    const inProgress = this.toEvidence(provisional, request, true);
    if (inProgress) out.push(inProgress);
    return out;
  }

  private refFor(events: ActualCostEvent[]): EvidenceObservation["ref"] {
    const first = events[0];
    if (!first) return null;
    return { kind: first.source === "expense" ? "expense" : "purchase", id: first.id };
  }

  private toEvidence(
    observations: EvidenceObservation[],
    request: EstimateRequest,
    provisional: boolean,
  ): CostEvidence | null {
    const stats = summarize(observations);
    if (!stats) return null;
    const market = request.market.zip;
    const explanation = [
      provisional
        ? `${stats.count} job${stats.count === 1 ? "" : "s"} where this work is under way — costs are not final.`
        : `${stats.count} completed job${stats.count === 1 ? "" : "s"} on record.`,
      "Median of what was actually paid, per unit, so one unusual job does not move the number.",
      "Excludes sales tax — the estimate adds estimating tax itself.",
    ];
    if (observations.some((o) => o.market && o.market !== market)) {
      explanation.push("Includes jobs in other markets; their ZIP is shown on each observation.");
    }
    return {
      id: `history:${provisional ? "provisional" : "complete"}:${request.subject.componentName ?? request.subject.featureDefinitionId}`,
      providerId: this.id,
      providerLabel: provisional ? "Company history (work in progress)" : "Company history",
      kind: this.kind,
      unitCostCentsX100: stats.medianX100,
      unit: request.subject.unit,
      lowCentsX100: stats.lowX100,
      highCentsX100: stats.highX100,
      sampleSize: stats.count,
      asOf: stats.latest,
      market: market || null,
      explanation,
      observations: observations.sort((a, b) => b.date.localeCompare(a.date)),
      quality: provisional ? "indicative" : "strong",
      provisional,
    };
  }

  /**
   * The component's effective quantity, from the assembly engine itself —
   * never a second quantity rule.
   */
  private async quantityFor(row: CandidateRow, cache: Map<string, Map<string, number>>): Promise<number | null> {
    if (row.instance_id === null || row.component_id === null) {
      // Feature-level subject: the feature's own quantity drives it.
      return row.feature_quantity_x100 === null ? null : Number(row.feature_quantity_x100);
    }
    let byComponent = cache.get(row.instance_id);
    if (!byComponent) {
      byComponent = new Map<string, number>();
      try {
        const calc = await this.assemblies.calculate(row.instance_id);
        for (const component of calc.components) {
          byComponent.set(component.componentId, component.effectiveQtyX100);
        }
      } catch {
        // A historical assembly whose quantity can no longer be resolved
        // (unit changed, feature quantity cleared) contributes nothing
        // rather than a wrong number.
      }
      cache.set(row.instance_id, byComponent);
    }
    return byComponent.get(row.component_id) ?? null;
  }

  /** Past generated lines for the same feature definition + component. */
  private async candidateLines(request: EstimateRequest): Promise<CandidateRow[]> {
    const { subject } = request;
    const excludeProject = request.projectId ?? "";
    if (subject.kind === "component") {
      return this.db.select<CandidateRow>(
        `SELECT l.id AS line_id, l.description, l.project_id,
                c.id AS component_id, c.instance_id, c.unit,
                f.name AS feature_name, f.work_status, f.quantity_x100 AS feature_quantity_x100,
                p.job_number, p.zip
           FROM proposed_cost_lines l
           JOIN assembly_instance_components c ON c.id = l.assembly_component_id
           JOIN assembly_instances i ON i.id = c.instance_id
           JOIN project_features f ON f.id = i.project_feature_id
           JOIN projects p ON p.id = l.project_id
          WHERE f.feature_definition_id = ?
            AND c.name = ?
            AND c.unit = ?
            AND l.project_id <> ?
            AND l.archived_at IS NULL
            AND c.archived_at IS NULL
            AND f.archived_at IS NULL`,
        [subject.featureDefinitionId, subject.componentName, subject.unit, excludeProject],
      );
    }
    return this.db.select<CandidateRow>(
      `SELECT l.id AS line_id, l.description, l.project_id,
              NULL AS component_id, NULL AS instance_id, f.quantity_unit AS unit,
              f.name AS feature_name, f.work_status, f.quantity_x100 AS feature_quantity_x100,
              p.job_number, p.zip
         FROM proposed_cost_lines l
         JOIN project_features f ON f.id = l.feature_id
         JOIN projects p ON p.id = l.project_id
        WHERE f.feature_definition_id = ?
          AND f.quantity_unit = ?
          AND l.project_id <> ?
          AND l.archived_at IS NULL
          AND f.archived_at IS NULL`,
      [subject.featureDefinitionId, subject.unit, excludeProject],
    );
  }
}

interface CandidateRow {
  line_id: string;
  description: string;
  project_id: string;
  component_id: string | null;
  instance_id: string | null;
  unit: string;
  feature_name: string;
  work_status: string | null;
  feature_quantity_x100: number | null;
  job_number: string;
  zip: string;
}
